import React, { useEffect } from "react"
import { useFormik } from "formik"
import * as yup from "yup"

import TextInput, { TextAreaInput } from "@components/ui/TextInput/TextInput"
import Button from "@components/ui/Button/Button"
import ErrorChip from "@components/ui/ErrorChip/ErrorChip"
import Loader from "@components/ui/Loader/BarLoader"

import styles from "./EditForm.module.sass"

export const userActionFormSchema = yup.object({
  name: yup
    .string()
    .required("Filed is required")
    .matches(/^[a-zA-Z_]+$/, "ONly characters a-z and _ allowed")
    .min(3, "Must be at least 3 chars length")
    .max(30, "Must be 30 chars maximum")
    .default("")
    .lowercase()
    .trim(),
  description: yup
    .string()
    .max(255, "Must be 255 chars maximum")
    .default("")
    .trim(),
})

export type FormData = yup.InferType<typeof userActionFormSchema>

interface EditActionFormProps {
  isLoading?: boolean
  className?: string
  errors?: FormData
  initialValues?: FormData
  onSubmit: (form: FormData) => void
}

const EditActionForm: React.FC<EditActionFormProps> = ({
  errors,
  isLoading,
  initialValues = userActionFormSchema.getDefault(),
  onSubmit,
}) => {
  const formik = useFormik<FormData>({
    initialValues,
    validationSchema: userActionFormSchema,
    onSubmit: (form: FormData) => {
      onSubmit(userActionFormSchema.cast(form))
    },
  })

  useEffect(() => {
    if (errors) formik.setErrors(errors)
  }, [errors])

  return (
    <form className={styles.root} onSubmit={formik.handleSubmit}>
      <div className={styles.form}>
        <TextInput
          autoFocus
          name="name"
          placeholder="Enter action name..."
          value={formik.values.name}
          withError={!!(formik.touched.name && formik.errors.name)}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          after={
            formik.touched.name &&
            formik.errors.name && (
              <div className={styles.after}>
                <ErrorChip
                  message={formik.errors.name}
                  messagePlacement="left"
                />
              </div>
            )
          }
        />
        <TextAreaInput
          name="description"
          placeholder="Add some description..."
          value={formik.values.description}
          withError={
            !!(formik.touched.description && formik.errors.description)
          }
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          after={
            formik.touched.description &&
            formik.errors.description && (
              <div className={styles.after}>
                <ErrorChip
                  message={formik.errors.description}
                  messagePlacement="left"
                />
              </div>
            )
          }
        />
        <Button type="submit" color="primary" disabled={isLoading}>
          Save {isLoading && <Loader />}
        </Button>
      </div>
    </form>
  )
}

export default EditActionForm
